import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ContactSupportDialog,
  SUPPORT_CATEGORY_LABELS,
  SUPPORT_STATUS_LABELS,
} from "@/components/contact-support-dialog";
import {
  listMySupportTickets,
  getSupportTicket,
  replySupportTicket,
} from "@/lib/support.functions";
import { Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/support")({
  component: SupportPage,
});

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  open: "default",
  in_progress: "default",
  waiting_on_user: "secondary",
  resolved: "outline",
  closed: "outline",
};

function SupportPage() {
  const listFn = useServerFn(listMySupportTickets);
  const { data, isLoading } = useQuery({
    queryKey: ["support-tickets-mine"],
    queryFn: () => listFn(),
  });
  const [newOpen, setNewOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Support tickets</h2>
          <p className="text-sm text-muted-foreground">
            Submit requests, questions, bugs, or feedback. We'll reply here and via email.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> New ticket
        </Button>
      </div>
      <div className="rounded-md border border-border">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading...</div>
        ) : !data?.tickets?.length ? (
          <div className="p-6 text-sm text-muted-foreground">
            No tickets yet. Need help with something? Click "New ticket".
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {data.tickets.map((t: any) => (
              <li
                key={t.id}
                className="flex cursor-pointer items-center justify-between p-4 hover:bg-muted/50"
                onClick={() => setActiveId(t.id)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{t.subject}</span>
                    {t.last_message_by_admin && t.status !== "closed" && t.status !== "resolved" && (
                      <Badge variant="default" className="h-5 text-[10px]">New reply</Badge>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {SUPPORT_CATEGORY_LABELS[t.category] ?? t.category} ·{" "}
                    {new Date(t.last_message_at).toLocaleString()}
                  </div>
                </div>
                <Badge variant={STATUS_VARIANT[t.status] ?? "outline"}>
                  {SUPPORT_STATUS_LABELS[t.status] ?? t.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ContactSupportDialog open={newOpen} onOpenChange={setNewOpen} />
      {activeId && (
        <TicketThreadDialog ticketId={activeId} onClose={() => setActiveId(null)} isAdmin={false} />
      )}
    </div>
  );
}

export function TicketThreadDialog({
  ticketId,
  onClose,
  isAdmin,
}: {
  ticketId: string;
  onClose: () => void;
  isAdmin: boolean;
}) {
  const qc = useQueryClient();
  const getFn = useServerFn(getSupportTicket);
  const replyFn = useServerFn(replySupportTicket);
  const { data, isLoading } = useQuery({
    queryKey: ["support-ticket", ticketId],
    queryFn: () => getFn({ data: { ticketId } }),
  });
  const [reply, setReply] = useState("");
  const mutation = useMutation({
    mutationFn: () => replyFn({ data: { ticketId, body: reply } }),
    onSuccess: () => {
      setReply("");
      qc.invalidateQueries({ queryKey: ["support-ticket", ticketId] });
      qc.invalidateQueries({ queryKey: ["support-tickets-mine"] });
      qc.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      toast.success("Reply sent");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isLoading ? "Loading..." : data?.ticket?.subject}
          </DialogTitle>
        </DialogHeader>
        {data?.ticket && (
          <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">
              {SUPPORT_CATEGORY_LABELS[data.ticket.category] ?? data.ticket.category}
            </Badge>
            <Badge variant={STATUS_VARIANT[data.ticket.status] ?? "outline"}>
              {SUPPORT_STATUS_LABELS[data.ticket.status] ?? data.ticket.status}
            </Badge>
          </div>
        )}
        <div className="max-h-[400px] space-y-3 overflow-y-auto rounded-md border border-border bg-muted/20 p-3">
          {data?.messages?.map((m: any) => (
            <div
              key={m.id}
              className={`rounded-md border p-3 text-sm ${
                m.is_admin_reply
                  ? "border-primary/30 bg-primary/5"
                  : "border-border bg-background"
              }`}
            >
              <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-medium">
                  {m.is_admin_reply ? "Support team" : (m.sender?.full_name || m.sender?.email || "User")}
                </span>
                <span>{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div className="whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={isAdmin ? "Reply to user..." : "Add a reply..."}
            rows={3}
            maxLength={5000}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              disabled={mutation.isPending || reply.trim().length < 1}
              onClick={() => mutation.mutate()}
            >
              Send reply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
